/**
 * Application entry. Wires bootstrap state machine, runtime config and router to durable backends.
 * ADMIN-T017 (D12-B): Pinia created early (setActivePinia) so the permission store is usable
 * during the bootstrap PermissionPriming phase before app.mount().
 */
import { createApp } from 'vue';
import { createPinia, setActivePinia } from 'pinia';
import AppShell from './app/AppShell.vue';
import { router } from './app/router';
import { runBootstrap, triggerReauth } from './app/bootstrap';
import { loadRuntimeConfig, type RuntimeConfig } from './config/runtimeConfig';
import { BUILD_CONFIG } from './config/buildConfig';
import { configureHttpClient } from './data/api/client';
import { runStartupCleanup } from './app/lifecycle/startupCleanup';
import { currentSession, expireSession, fetchPermissions, restoreSessionFromStorage } from './auth/session';
import { usePermissionStore } from './permission/store';
import './styles.css';

async function main(): Promise<void> {
  // Create and activate Pinia early so PermissionStore is usable during bootstrap.
  const pinia = createPinia();
  setActivePinia(pinia);
  restoreSessionFromStorage();

  try {
    runStartupCleanup({
      storage: window.localStorage,
      pathname: window.location.pathname,
    });
  } catch (err) {
    console.warn('[startup] cleanup failed', err);
  }

  // Initial HttpClient config — overwritten after runtime config loads.
  // getToken provides automatic Bearer injection for all /admin-bff calls.
  const getToken = () => {
    try { return usePermissionStore().token ?? null; }
    catch { return null; }
  };

  configureHttpClient({
    coreBaseUrl: BUILD_CONFIG.api?.coreBaseUrl ?? '/admin-bff',
    biBaseUrl: BUILD_CONFIG.api?.biBaseUrl ?? '/admin-bff/bi',
    getToken,
    onAuthExpired: () => {
      expireSession();
      triggerReauth();
    },
  });

  let runtime: RuntimeConfig | null = null;

  await runBootstrap({
    loadRuntimeConfig: async () => {
      runtime = await loadRuntimeConfig();
      configureHttpClient({
        coreBaseUrl: runtime.api.coreBaseUrl,
        biBaseUrl: runtime.api.biBaseUrl,
        getToken,
        onAuthExpired: () => {
          expireSession();
          triggerReauth();
        },
      });
    },
    // Same-tab refresh restores the login envelope first; permission priming validates it.
    resolveSession: async () => ({ authenticated: currentSession.value !== null }),
    // Refresh permission codes from server (called only when resolveSession returns true).
    primePermissions: async () => {
      await fetchPermissions();
    },
  });

  const app = createApp(AppShell);
  app.use(pinia); // reuse the same pinia instance
  app.use(router);
  app.mount('#app');
}

void main();
